import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, runInAction, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as Sentry from '@sentry/react';

import autobind from 'autobind-decorator';
import {withI18n} from 'spa/Components/Context';
import isValidException from 'utils/isValidException';

import CardOptionItem from './CardOptionItem';

import AddPaymentMethodForm from 'spa/Pages/BookingNext/Components/AddPaymentMethodForm/AddPaymentMethodForm';

import {PAYMENT_TYPES} from '../../state/payments/billing';

import '../CheckoutPaymentDetails/checkout-payment-details.less';

import plus from 'spa/images/icons/plus.svg';

@inject(({
  store: {
    billing,
    notifications,
    session,
    tracker,
  },
}) =>
  ({
    billing,
    notifications,
    session,
    tracker,
  }))
@observer
@withI18n
class CardOption extends React.Component {
  static propTypes = {
    billing: PropTypes.shape({
      _add: PropTypes.object,
      add: PropTypes.func.isRequired,
      addNewCard: PropTypes.func.isRequired,
      addNewCardV2: PropTypes.func.isRequired,
      cardGateway: PropTypes.shape({
        gateway: PropTypes.string.isRequired,
      }),
      cards: PropTypes.array.isRequired,
      paymentInfo: PropTypes.shape({
        paymentOption: PropTypes.string,
        paymentToken: PropTypes.string,
      }).isRequired,
      tokenizeCard: PropTypes.func.isRequired,
      setPaymentOption: PropTypes.func.isRequired,
      setPaymentToken: PropTypes.func.isRequired,
      updatePaymentMethodChanged: PropTypes.func.isRequired,
    }).isRequired,
    disabled: PropTypes.bool,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    onChangeToken: PropTypes.func.isRequired,
    session: PropTypes.shape({
      user: PropTypes.object,
    }).isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
  };

  static defaultProps = {
    disabled: false,
  };

  @observable cardState = {};
  @observable newCardForm = false;
  @observable processingForm = false;

  @autobind
  @action('onChangeToken - CardOption')
  onChangeToken(event) {
    const {
      onChangeToken,
    } = this.props;

    this.newCardForm = false;

    onChangeToken(event);
  }

  @autobind
  @action('onClickAddNewCard - CardOption')
  onClickAddNewCard() {
    const {
      billing: {
        setPaymentToken,
        updatePaymentMethodChanged,
      },
    } = this.props;

    setPaymentToken('');
    updatePaymentMethodChanged(true);

    this.newCardForm = true;
  }

  /*
  * Submit the add payment method form
  */
  @autobind
  @action('onReceiveForm - CardOption')
  onReceiveForm(event) {
    event.preventDefault();
    if (this.processingForm) return null;

    this.processingForm = true;

    const {
      billing,
      session,
      tracker,
      trackingCategory,
    } = this.props;

    const {
      'card-number': number,
      'card-cvv': cvv,
      'card-expiration': expirationDate,
      'card-postcode': postalCode,
    } = this.cardState;

    const name = this.props.session.user && this.props.session.user.name;

    const creditCardData = {
      name,
      number,
      expirationMonth: expirationDate.split('/')[0],
      expirationYear: expirationDate.split('/')[1],
      cvv,
      billingAddress: {
        postalCode,
      },
    };

    tracker.track('User submitted form to add new payment method', {
      category: trackingCategory,
    });

    billing.addNewCard(
      creditCardData,
      session.user,
      this.onAddCardSuccess,
      this.onAddCardError,
      this.onReceiveChallengeAddCard,
    );
  }

  @autobind
  @action('onAddNewPaymentMethod - CheckoutScreen')
  onAddNewPaymentMethodV2(cardData) {
    const {
      billing,
      session,
      tracker,
    } = this.props;

    this.isProcessingForm = true;

    tracker.track('User submitted form to add new payment method', {
      category: 'Booking',
    });

    billing.addNewCardV2(
      cardData,
      session.user,
      this.onAddCardSuccess,
      this.onAddCardError,
      this.onReceiveChallengeAddCard,
    );
  }

  @autobind
  @action('onAddCardSuccess - CardOption')
  onAddCardSuccess(response) {
    const {i18n} = this.context;
    const {
      billing,
      notifications,
      tracker,
      trackingCategory,
    } = this.props;

    tracker.track('Added Payment Method', {
      category: trackingCategory,
    });

    notifications.add({
      id: 'success-card',
      type: 'success',
      title: i18n._x('Your payment method has been successfully added.', 'Add payment method page'),
    });

    runInAction(() => {
      this.processingForm = false;
      this.newCardForm = false;
    });

    // select the most recent card
    billing.setPaymentOption(PAYMENT_TYPES.CARD);
    billing.setPaymentToken(response.paymentMethod_id);
  }

  @autobind
  @action('onAddCardError - CardOption')
  onAddCardError(error) {
    const {i18n} = this.context;
    const {
      notifications,
      tracker,
      trackingCategory,
    } = this.props;

    tracker.track('Failed to Add Payment Method', {
      category: trackingCategory,
      charge_id: error.chargeId || null,
    });

    notifications.add({
      id: 'card-option-add-card-error',
      type: 'error',
      title: i18n.__error(error),
    });

    runInAction(() => {
      this.processingForm = false;
    });

    if (i18n.isKnownError(error)) return;

    // This is unexpected so record it in Sentry
    if (isValidException(error)) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage('CardOption - unhandled error on add payment method');
    }
  }

  @autobind
  @action('onReceiveChallengeAddCard - CardOption')
  onReceiveChallengeAddCard() {
    const {
      tracker,
      trackingCategory,
    } = this.props;

    tracker.track('Add Payment Method Received Challenge', {
      category: trackingCategory,
    });
  }

  @computed get cardsList() {
    const {i18n} = this.context;
    const {billing, disabled} = this.props;

    if (billing.cards && billing.cards.length) {
      return billing.cards.map(card => (
        <CardOptionItem
          cardInfo={card}
          disabled={disabled}
          i18n={i18n}
          key={`card-item-${card.cardLast4}`}
          onChangeToken={this.onChangeToken}
          paymentInfo={billing.paymentInfo}
        />
      ));
    }

    return null;
  }

  render() {
    const {
      cardsList,
      context,
      props,
      newCardForm,
    } = this;
    const {i18n} = context;
    const {
      billing,
      disabled,
    } = props;
    const {paymentInfo} = billing;

    return (
      <div data-testid="card-option-container">
        {cardsList}
        {billing.cards.length > 0 &&
          <label className="checkout-payment-details__payment-method">
            <input
              type="radio"
              name="payment-token"
              defaultValue=""
              onChange={this.onClickAddNewCard}
              checked={paymentInfo.paymentToken === ''}
              disabled={disabled}
            />
            <span className="checkout-payment-details__payment-method-icon">
              <img src={plus} alt="" />
            </span>
            <span className="checkout-payment-details__payment-method-label">
              {i18n._x('Add new card', 'Gifts Checkout Payment Details')}
            </span>
          </label>
        }
        {(newCardForm || (billing.cards.length === 0 && billing.cardGateway)) &&
          <div data-testid="card-option-new-card-container">
            <AddPaymentMethodForm
              hideCancelAdd
              isProcessingForm={this.isProcessingForm}
              billing={billing}
              onAddNewPaymentMethod={this.onAddNewPaymentMethodV2}
            />
          </div>
        }
      </div>
    );
  }
}

export default CardOption;



// WEBPACK FOOTER //
// ./spa/Components/AddPaymentMethod/CardOption.jsx